/**
 * HOY — la pantalla más importante.
 *
 * Responde en menos de cinco segundos qué toca, cuánto dura, qué entrena, por
 * qué, y qué hacer ahora. El CTA principal es uno solo.
 *
 * REGLA DURA: «Empezar sesión» no navega hasta que la escritura terminó. Nunca
 * se entra al modo foco con una sesión que solo existe en memoria.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { iniciarSesion, claveDeRecomendacion } from '../../app/servicioSesiones';
import { CURRICULUM_ACTUAL } from '../../curriculum';
import type { ClaveSesion } from '../../curriculum/tipos';
import { programaInicialCompletado } from '../../domain/reglas/checkpoints';
import { bloqueDeSemana } from '../../curriculum/v1/enfasis';
import type { DuracionSesion } from '../../domain/modelo/ajustes';
import {
  baselineCompletado,
  checkpointPendiente,
  diasHechosDelCiclo,
  semanaActual,
  siguienteRecomendada,
  CLAVES_DIA_ORDENADAS,
} from '../../domain/reglas/progresion';
import { baselineIncompleto } from '../../domain/reglas/baseline';
import { estadoFinalDe, resumirSesion } from '../../domain/reglas/sesionActiva';
import { ajustesRepo } from '../../persistencia/ajustesRepo';
import { sesionesRepo } from '../../persistencia/sesionesRepo';
import { Pantalla } from '../comunes/Pantalla';
import { CapturaRapida } from './CapturaRapida';
import { Suave, Tarjeta } from '../comunes/Tarjeta';
import estilos from './Hoy.module.css';

const DURACIONES: DuracionSesion[] = [20, 35, 50];

export function Hoy() {
  const navegar = useNavigate();
  const sesiones = useLiveQuery(() => sesionesRepo.listar(), [], undefined);
  const ajustes = useLiveQuery(() => ajustesRepo.obtener(), [], undefined);
  const [arrancando, setArrancando] = useState(false);
  /**
   * «Ahora no» vive SOLO en memoria, a propósito.
   *
   * Persistirlo convertiría un aplazamiento en un estado que se puede olvidar
   * para siempre. Al volver a Hoy la recomendación reaparece, que es lo que
   * significa «recomendar sin bloquear».
   */
  const [aplazado, setAplazado] = useState(false);

  if (!sesiones || !ajustes) {
    return (
      <Pantalla titulo="Hoy">
        <Suave>Cargando…</Suave>
      </Pantalla>
    );
  }

  const semana = semanaActual(sesiones);
  const bloque = bloqueDeSemana(semana);
  const recomendacion = siguienteRecomendada(sesiones);
  const enProgreso = recomendacion.tipo === 'reanudar' ? recomendacion.sesion : null;
  const sesion = CURRICULUM_ACTUAL.sesiones[claveDeRecomendacion(recomendacion)];
  const hechos = diasHechosDelCiclo(sesiones);
  const checkpoint = checkpointPendiente(sesiones);
  const programaCompletado = programaInicialCompletado(sesiones);
  const baselineAMedias = baselineIncompleto(sesiones);
  const duracion = ajustes.duracionPreferida;
  const plan = sesion.planes[duracion];

  /** Herramientas DERIVADAS de los pasos reales de esta duración. */
  const herramientas = [
    ...new Set(plan.pasos.map((paso) => paso.herramienta).filter((h) => h !== 'ninguna')),
  ];

  async function empezar() {
    setArrancando(true);
    try {
      // La sesión queda PERSISTIDA antes de navegar. Si esto falla, el aviso
      // global de escritura fallida lo hace visible y no se entra al foco.
      await iniciarSesion(duracion);
      navegar('/sesion');
    } finally {
      setArrancando(false);
    }
  }

  async function terminarAqui() {
    if (!enProgreso) return;
    // «Terminar aquí» NO borra nada: registra el estado que corresponde y
    // conserva pasos, notas y tiempos.
    await sesionesRepo.guardar({
      ...enProgreso,
      estado: estadoFinalDe(enProgreso),
      completadaEn: new Date().toISOString(),
      segundosReales: resumirSesion(enProgreso).segundos,
    });
  }

  return (
    <Pantalla titulo="Hoy">
      <div className={estilos.cabecera}>
        <span className={estilos.semana}>
          {programaCompletado ? 'Programa completado' : `Semana ${semana}`}
        </span>
        {bloque ? <span className={estilos.bloque}>{bloque.titulo}</span> : null}
      </div>

      {enProgreso ? (
        <div className={estilos.reanudar}>
          <p className={estilos.reanudarTitulo}>Tenés una sesión en progreso.</p>
          <Suave>
            {CURRICULUM_ACTUAL.sesiones[enProgreso.sessionKey as ClaveSesion]?.titulo ??
              enProgreso.sessionKey}{' '}
            · {enProgreso.duracionPlanificada} min · paso {enProgreso.pasoActual + 1} de{' '}
            {enProgreso.pasos.length}
          </Suave>
          <div className={estilos.reanudarAcciones}>
            <button
              type="button"
              className={estilos.cta}
              onClick={() => navegar('/sesion')}
            >
              Reanudar
            </button>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => void terminarAqui()}
            >
              Terminar aquí
            </button>
          </div>
        </div>
      ) : null}

      <CapturaRapida />

      <ol className={estilos.ciclo} aria-label="Progreso del ciclo de 7 sesiones">
        {CLAVES_DIA_ORDENADAS.map((clave, indice) => {
          const hecho = hechos.has(clave);
          return (
            <li
              key={clave}
              className={hecho ? `${estilos.dia} ${estilos.diaHecho}` : estilos.dia}
              aria-label={`Día ${indice + 1}${hecho ? ', hecho' : ', pendiente'}`}
            >
              {hecho ? '✓' : indice + 1}
            </li>
          );
        })}
      </ol>

      {checkpoint !== null ? (
        <Tarjeta>
          <p className={estilos.checkpoint}>
            Te corresponde el Checkpoint de Semana {checkpoint}.
          </p>
          <Suave>
            Conviene medir antes de seguir con la semana {checkpoint + 1}, pero no
            bloquea nada. La recomendación se queda mientras siga pendiente: «Ahora no»
            la aparta hoy, no la cancela.
          </Suave>
          {aplazado ? (
            <p className={estilos.aplazado}>
              Sigue pendiente. Va a volver a aparecer hasta que lo hagas.
            </p>
          ) : (
            <div className={estilos.reanudarAcciones}>
              <button
                type="button"
                className={estilos.cta}
                disabled={arrancando}
                onClick={() => void empezar()}
              >
                Hacer checkpoint
              </button>
              <button
                type="button"
                className={estilos.secundario}
                onClick={() => setAplazado(true)}
              >
                Ahora no
              </button>
            </div>
          )}
        </Tarjeta>
      ) : null}

      <Tarjeta titulo={sesion.titulo}>
        <p className={estilos.objetivo}>{sesion.objetivo}</p>

        <details className={estilos.porQue}>
          <summary>¿Por qué hacemos esto?</summary>
          <p>{sesion.porQue}</p>
        </details>

        <fieldset className={estilos.duraciones} disabled={Boolean(enProgreso)}>
          <legend className={estilos.leyenda}>¿Cuánto tiempo tenés?</legend>
          <div className={estilos.opciones}>
            {DURACIONES.map((opcion) => (
              <label
                key={opcion}
                className={
                  duracion === opcion
                    ? `${estilos.opcion} ${estilos.opcionActiva}`
                    : estilos.opcion
                }
              >
                <input
                  type="radio"
                  name="duracion"
                  value={opcion}
                  checked={duracion === opcion}
                  onChange={() => void ajustesRepo.guardar({ duracionPreferida: opcion })}
                  className="sr-only"
                />
                {opcion} min
              </label>
            ))}
          </div>
        </fieldset>

        <Suave>
          El día define qué entrenás. El tiempo disponible solo define cuánto profundizás.
        </Suave>

        {herramientas.length > 0 ? (
          <div className={estilos.herramientas}>
            <h3 className={estilos.subtitulo}>Herramientas de esta versión</h3>
            <ul>
              {herramientas.map((h) => (
                <li key={h}>{nombreHerramienta(h)}</li>
              ))}
            </ul>
          </div>
        ) : (
          <Suave>Esta versión de la sesión no necesita ninguna herramienta externa.</Suave>
        )}

        <ol className={estilos.pasos}>
          {plan.pasos.map((paso) => (
            <li key={paso.clave}>
              <strong>{paso.titulo}</strong>
              <span className={estilos.minutos}>
                {paso.minutosSugeridos === null
                  ? 'sin tiempo'
                  : `${paso.minutosSugeridos} min sugeridos`}
              </span>
            </li>
          ))}
        </ol>

        {!enProgreso ? (
          <button
            type="button"
            className={estilos.cta}
            onClick={() => void empezar()}
            disabled={arrancando}
          >
            {arrancando ? 'Preparando…' : 'Empezar sesión'}
          </button>
        ) : null}

      </Tarjeta>

      {baselineAMedias ? (
        <Tarjeta titulo="Tu baseline quedó a medias">
          <Suave>
            Lo empezaste pero no lo terminaste: quedó como{' '}
            {baselineAMedias.estado === 'parcial' ? 'parcial' : 'abandonado'}, así que
            el programa todavía no puede arrancar sobre él.
          </Suave>
          <Suave>
            El baseline decide cuánto peso tendrán listening, pronunciación,
            recuperación léxica y conversación durante doce semanas. Darlo por hecho a
            medias sería entrenar contra datos inventados.
          </Suave>
          <Suave>Volvé a hacerlo cuando tengas el rato y el lugar tranquilo.</Suave>
        </Tarjeta>
      ) : !baselineCompletado(sesiones) ? (
        <Tarjeta>
          <Suave>
            Todavía no hiciste el baseline. Va antes del Día 1: mide para no entrenar
            problemas imaginarios.
          </Suave>
        </Tarjeta>
      ) : null}
    </Pantalla>
  );
}

function nombreHerramienta(clave: string): string {
  if (clave === 'segunFoco') return 'Según tu foco de hoy';
  const ficha = CURRICULUM_ACTUAL.herramientas.find((h) => h.clave === clave);
  return ficha ? `${ficha.icono} ${ficha.nombre}` : clave;
}
